// Remote Support sidebar (header-toggled, right-edge dock like DoctorChatPopup).
// Owner view: add support agents by email, list/remove them, and see who is
// in the live session right now. Agent view: session info + roster only.
// Realtime state (roster) comes in via props from App.js; the grant list is
// fetched/managed here through lib/supportAccess.js.

const React = require('react');
const { createElement: h, useState, useEffect } = React;
const { listSupportAgents, addSupportAgent, removeSupportAgent } = require('../lib/supportAccess');
require('./RemoteSupportPopup.css');

function RemoteSupportPopup(props) {
  var isAgent = !!props.isAgent;
  var ownerUsername = props.ownerUsername || '';
  var roster = Array.isArray(props.roster) ? props.roster : [];
  var selfSessionId = props.selfSessionId || '';
  var onClose = typeof props.onClose === 'function' ? props.onClose : function() {};

  var [agents, setAgents] = useState([]);
  var [loadingAgents, setLoadingAgents] = useState(false);
  var [identifier, setIdentifier] = useState('');
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState('');
  var [success, setSuccess] = useState('');

  useEffect(function() {
    if (isAgent) return;
    var mounted = true;
    setLoadingAgents(true);
    listSupportAgents()
      .then(function(list) { if (mounted) setAgents(list); })
      .catch(function() {})
      .finally(function() { if (mounted) setLoadingAgents(false); });
    return function() { mounted = false; };
  }, [isAgent]);

  function handleAdd() {
    var value = String(identifier || '').trim();
    if (!value || busy) return;
    setBusy(true);
    setError('');
    setSuccess('');
    addSupportAgent(value)
      .then(function(agent) {
        if (agent) setAgents(function(prev) { return prev.concat([agent]); });
        setIdentifier('');
        setSuccess('Added ' + (agent && agent.agentLabel ? agent.agentLabel : value) + ' as a support agent.');
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not add that account.');
      })
      .finally(function() { setBusy(false); });
  }

  function handleRemove(agentUserId) {
    if (busy) return;
    setBusy(true);
    setError('');
    setSuccess('');
    removeSupportAgent(agentUserId)
      .then(function() {
        setAgents(function(prev) {
          return prev.filter(function(agent) { return agent.agentUserId !== agentUserId; });
        });
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not remove that agent.');
      })
      .finally(function() { setBusy(false); });
  }

  var others = roster.filter(function(member) { return member && member.sessionId !== selfSessionId; });

  function renderRoster() {
    return h('div', { className: 'remote-support-section' },
      h('div', { className: 'remote-support-section-title' },
        'In this session',
        roster.length ? h('span', { className: 'remote-support-live-dot', title: 'Live' }) : null
      ),
      roster.length === 0
        ? h('div', { className: 'remote-support-empty' }, 'Connecting…')
        : h('div', { className: 'remote-support-roster' },
            roster.map(function(member) {
              return h('div', { className: 'remote-support-member', key: member.sessionId },
                h('span', { className: 'remote-support-member-dot', style: { background: member.color || '#7c5cff' } }),
                h('span', { className: 'remote-support-member-name' },
                  member.username + (member.sessionId === selfSessionId ? ' (you)' : '')
                ),
                h('span', { className: 'remote-support-member-role' }, member.role === 'owner' ? 'Owner' : 'Support')
              );
            })
          ),
      (!isAgent && others.length === 0)
        ? h('div', { className: 'remote-support-hint' },
            'Support agents you add below can join from their dashboard while you have this workflow open.')
        : null
    );
  }

  function renderOwnerBody() {
    return h(React.Fragment, null,
      renderRoster(),
      h('div', { className: 'remote-support-section' },
        h('div', { className: 'remote-support-section-title' }, 'Support agents'),
        h('div', { className: 'remote-support-add-row' },
          h('input', {
            className: 'remote-support-input',
            type: 'text',
            placeholder: 'Email of the Alleflow account',
            value: identifier,
            disabled: busy,
            onChange: function(e) { setIdentifier(e.target.value); },
            onKeyDown: function(e) { if (e.key === 'Enter') handleAdd(); }
          }),
          h('button', {
            className: 'remote-support-add-btn',
            onClick: handleAdd,
            disabled: busy || !String(identifier || '').trim()
          }, busy ? '…' : 'Add')
        ),
        error ? h('div', { className: 'remote-support-error' }, error) : null,
        success ? h('div', { className: 'remote-support-success' }, success) : null,
        loadingAgents
          ? h('div', { className: 'remote-support-empty' }, 'Loading…')
          : (agents.length === 0
              ? h('div', { className: 'remote-support-empty' }, 'No support agents yet.')
              : h('div', { className: 'remote-support-agent-list' },
                  agents.map(function(agent) {
                    return h('div', { className: 'remote-support-agent-row', key: agent.agentUserId },
                      h('span', { className: 'remote-support-agent-label' }, agent.agentLabel),
                      h('button', {
                        className: 'remote-support-remove-btn',
                        title: 'Remove this support agent',
                        disabled: busy,
                        onClick: function() { handleRemove(agent.agentUserId); }
                      },
                        h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' },
                          h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
                          h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
                        )
                      )
                    );
                  })
                )),
        h('div', { className: 'remote-support-hint' },
          'Agents get access to your live editing sessions only — they can never open your workflows while you are away, and only you can save.')
      )
    );
  }

  function renderAgentBody() {
    return h(React.Fragment, null,
      h('div', { className: 'remote-support-section' },
        h('div', { className: 'remote-support-agent-banner' },
          'You are supporting ',
          h('strong', null, ownerUsername || 'the owner'),
          ' in their live session. Edits sync to their canvas — only they can save.'
        )
      ),
      renderRoster()
    );
  }

  return h('div', { className: 'remote-support-popup' },
    h('div', { className: 'remote-support-header' },
      h('div', { className: 'remote-support-title' }, 'Remote Support'),
      h('button', { className: 'remote-support-close', onClick: onClose, title: 'Close' },
        h('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' },
          h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
          h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
        )
      )
    ),
    h('div', { className: 'remote-support-body' },
      isAgent ? renderAgentBody() : renderOwnerBody()
    )
  );
}

module.exports = RemoteSupportPopup;
